import { BeforeInstallPromptEvent, PWAInstallState, NetworkState } from '../types/pwa'

class PWAService {
  private installPrompt: BeforeInstallPromptEvent | null = null
  private listeners: Set<() => void> = new Set()

  constructor() {
    this.initInstallPrompt()
    this.initNetworkMonitoring()
  }

  // 安装提示相关
  private initInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      this.installPrompt = e as BeforeInstallPromptEvent
      this.notifyListeners()
    })

    window.addEventListener('appinstalled', () => {
      this.installPrompt = null
      this.notifyListeners()
    })
  }

  getInstallState(): PWAInstallState {
    return {
      isInstallable: !!this.installPrompt,
      isInstalled: window.matchMedia('(display-mode: standalone)').matches,
      installPrompt: this.installPrompt,
      platform: this.getPlatform()
    }
  }

  async installPWA(): Promise<boolean> {
    if (!this.installPrompt) return false

    try {
      await this.installPrompt.prompt()
      const { outcome } = await this.installPrompt.userChoice
      this.installPrompt = null
      return outcome === 'accepted'
    } catch (error) {
      console.error('PWA安装失败:', error)
      return false
    }
  }

  // 通知相关
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied'
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      return permission
    }

    return Notification.permission
  }

  showNotification(title: string, options?: NotificationOptions): boolean {
    if (Notification.permission !== 'granted') return false

    try {
      new Notification(title, {
        icon: '/icon-192x192.svg',
        badge: '/icon-192x192.svg',
        ...options
      })
      return true
    } catch (error) {
      console.error('显示通知失败:', error)
      return false
    }
  }

  // 网络状态监控
  private initNetworkMonitoring() {
    window.addEventListener('online', () => this.notifyListeners())
    window.addEventListener('offline', () => this.notifyListeners())
  }

  getNetworkState(): NetworkState {
    return {
      isOnline: navigator.onLine,
      connectionType: (navigator as any).connection?.effectiveType
    }
  }

  // 通用监听器管理
  subscribe(callback: () => void) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  private notifyListeners() {
    this.listeners.forEach(callback => callback())
  }

  private getPlatform(): string {
    const userAgent = navigator.userAgent.toLowerCase()
    if (userAgent.includes('android')) return 'android'
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) return 'ios'
    if (userAgent.includes('win')) return 'windows'
    if (userAgent.includes('mac')) return 'macos'
    return 'unknown'
  }
}

export const pwaService = new PWAService()